import { useMemo } from 'react';
import { GlsOrg } from '@wetags/gls';
import { useListWorkspacesInfiniteQuery } from '@frontend/api-workspaces';

export interface WorkspaceState {
  /** Whether workspace section should be rendered */
  shouldShowWorkspaces: boolean;
  /** Whether workspaces are currently loading */
  isWorkspacesLoading: boolean;
  /** Whether workspace feature is enabled (wetag) */
  isWorkspacesEnabled: boolean;
  /** Whether org has any workspaces */
  hasWorkspaces: boolean;
  /** Whether workspace data has been fetched at least once (for cache detection) */
  hasFetchedOnce: boolean;
}

/**
 * Manages workspace visibility and loading state for location pickers.
 * Uses React Query cache to prevent layout shift on subsequent renders.
 *
 * @param orgId - Organization ID to check workspaces for
 * @returns WorkspaceState object with visibility and loading flags
 */
export function useWorkspaceState(orgId: string): WorkspaceState {
  const [isWorkspacesEnabled, isWetagLoading] = GlsOrg.useWetag('workspace');
  // Only fetch the workspace list once the wetag confirms the feature is enabled.
  // Fetching unconditionally means every multi-location user who hits the location
  // picker fires a workspace list request even for orgs that have the feature off.
  const { data, isLoading } = useListWorkspacesInfiniteQuery(orgId, {
    enabled: !!isWorkspacesEnabled,
  });

  const workspaceCount = useMemo(() => data?.pages.flatMap((p) => p.workspaces ?? []).length ?? 0, [data]);

  const hasFetchedOnce = data !== undefined;
  const hasWorkspaces = workspaceCount > 0;

  // Show loading ONLY during first load (before cache exists)
  const isWorkspacesLoading = isLoading && !hasFetchedOnce;

  // Show workspace section if wetag enabled AND (has workspaces OR still first load)
  const shouldShowWorkspaces =
    !isWetagLoading && isWorkspacesEnabled && (hasWorkspaces || (isLoading && !hasFetchedOnce));

  return {
    shouldShowWorkspaces,
    isWorkspacesLoading,
    isWorkspacesEnabled,
    hasWorkspaces,
    hasFetchedOnce,
  };
}
